/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, Check, X, Loader2 } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

interface OrgEntry {
  id: string;
  name: string;
  slug: string | null;
  role: string;
  joined_at: string;
}

export function OrgManager({
  initialOrgs,
  activeOrgId,
}: {
  initialOrgs: OrgEntry[];
  activeOrgId?: string | null;
}): import('react/jsx-runtime').JSX.Element {
  const [orgs, setOrgs] = useState<OrgEntry[]>(initialOrgs);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [currentOrgId, setCurrentOrgId] = useState(activeOrgId ?? initialOrgs[0]?.id ?? null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  async function switchOrg(orgId: string): Promise<void> {
    if (orgId === currentOrgId) return;
    setSwitching(orgId);
    await fetch('/api/orgs/switch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgId }),
    });
    setCurrentOrgId(orgId);
    setSwitching(null);
    // Refresh the whole app so the layout re-reads the cookie
    navigate('/');
    window.location.reload();
  }

  async function createOrg(): Promise<void> {
    const name = newName.trim();
    if (!name) return;
    setLoading(true);
    setError(null);

    const res = await fetch('/api/orgs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    const data = await res.json();
    setLoading(false);

    if (!res.ok) {
      setError(data.error ?? 'Failed to create organisation');
      return;
    }

    setOrgs((prev): OrgEntry[] => [...prev, { ...(data as OrgEntry), role: 'owner' }]);
    setNewName('');
    setCreating(false);
    window.location.reload();
  }

  const roleColor: Record<string, string> = {
    owner: 'bg-[var(--flux-accent)]/15 text-[var(--flux-accent)] border-[var(--flux-accent)]/30',
    engineer: 'bg-[var(--flux-fetch)]/15 text-[var(--flux-fetch)] border-[var(--flux-fetch)]/30',
    viewer: 'bg-secondary text-secondary-foreground',
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-sm">Organisations</CardTitle>
        {!creating && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 text-xs"
            onClick={(): void => {
              setCreating(true);
              setError(null);
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            New
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {orgs.length === 0 && !creating && (
          <p className="text-xs text-muted-foreground py-1">No organisations yet.</p>
        )}

        {orgs.map((org): import('react/jsx-runtime').JSX.Element => {
          const isActive = org.id === currentOrgId;
          const isSwitching = switching === org.id;
          return (
            <button
              key={org.id}
              onClick={(): Promise<void> => switchOrg(org.id)}
              className={`w-full flex items-center justify-between rounded-md border px-3 py-2 text-left transition-all ${
                isActive
                  ? 'border-[var(--flux-accent)]/40 bg-[var(--flux-accent)]/5 ring-1 ring-[var(--flux-accent)]/20'
                  : 'border-border hover:border-border/80 hover:bg-accent/50 cursor-pointer'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Building2
                  className={`h-3.5 w-3.5 shrink-0 ${isActive ? 'text-[var(--flux-accent)]' : 'text-muted-foreground'}`}
                />
                <span className="text-sm truncate">{org.name}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-2">
                <Badge variant="outline" className={`text-[10px] ${roleColor[org.role] ?? ''}`}>
                  {org.role}
                </Badge>
                {isSwitching ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                ) : isActive ? (
                  <Check className="h-3.5 w-3.5 text-[var(--flux-accent)]" />
                ) : null}
              </div>
            </button>
          );
        })}

        {creating && (
          <div className="space-y-2 pt-1">
            <div className="flex gap-2">
              <Input
                autoFocus
                placeholder="Organisation name"
                value={newName}
                onChange={(e): void => setNewName(e.target.value)}
                onKeyDown={(e): void => {
                  if (e.key === 'Enter') void createOrg();
                  if (e.key === 'Escape') {
                    setCreating(false);
                    setNewName('');
                    setError(null);
                  }
                }}
                className="h-8 text-sm"
              />
              <Button
                size="sm"
                className="h-8 px-2"
                disabled={!newName.trim() || loading}
                onClick={createOrg}
              >
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2"
                onClick={(): void => {
                  setCreating(false);
                  setNewName('');
                  setError(null);
                }}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
            {error != null && <p className="text-xs text-destructive">{error}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
